<div class="add-employee-container p-6 max-w-4xl mx-auto">
  <!-- Header -->
  <div class="mb-8">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Edit Employee</h1>
        <p class="text-gray-600 mt-2">Update employee information using the form below</p>
      </div>
      <app-sl-button
        variant="default"
        prefixIcon="x"
        (buttonClick)="cancel()">
        Cancel
      </app-sl-button>
    </div>
  </div>

  <!-- Progress Indicator -->
  <div class="mb-8">
    <div class="flex items-center">
      <!-- Step 1: Personal Details -->
      <div class="flex items-center">
        <div class="flex items-center justify-center w-8 h-8 rounded-full"
             [class.bg-blue-600]="currentStep() >= 1"
             [class.text-white]="currentStep() >= 1"
             [class.bg-gray-200]="currentStep() < 1"
             [class.text-gray-600]="currentStep() < 1">
          1
        </div>
        <span class="ml-2 text-sm font-medium"
              [class.text-blue-600]="currentStep() === 1"
              [class.text-gray-900]="currentStep() > 1"
              [class.text-gray-500]="currentStep() < 1">
          Personal Details
        </span>
      </div>
      
      <div class="flex-1 mx-2">
        <div class="h-1 rounded-full"
             [class.bg-blue-600]="currentStep() > 1"
             [class.bg-gray-200]="currentStep() <= 1">
        </div>
      </div>
      
      <!-- Step 2: Job Details -->
      <div class="flex items-center">
        <div class="flex items-center justify-center w-8 h-8 rounded-full"
             [class.bg-blue-600]="currentStep() >= 2"
             [class.text-white]="currentStep() >= 2"
             [class.bg-gray-200]="currentStep() < 2"
             [class.text-gray-600]="currentStep() < 2">
          2
        </div>
        <span class="ml-2 text-sm font-medium"
              [class.text-blue-600]="currentStep() === 2"
              [class.text-gray-900]="currentStep() > 2"
              [class.text-gray-500]="currentStep() < 2">
          Job Details
        </span>
      </div>
      
      <div class="flex-1 mx-2">
        <div class="h-1 rounded-full"
             [class.bg-blue-600]="currentStep() > 2"
             [class.bg-gray-200]="currentStep() <= 2">
        </div>
      </div>
      
      <!-- Step 3: Payee Details -->
      <div class="flex items-center">
        <div class="flex items-center justify-center w-8 h-8 rounded-full"
             [class.bg-blue-600]="currentStep() >= 3"
             [class.text-white]="currentStep() >= 3"
             [class.bg-gray-200]="currentStep() < 3"
             [class.text-gray-600]="currentStep() < 3">
          3
        </div>
        <span class="ml-2 text-sm font-medium"
              [class.text-blue-600]="currentStep() === 3"
              [class.text-gray-900]="currentStep() > 3"
              [class.text-gray-500]="currentStep() < 3">
          Payee Details
        </span>
      </div>
      
      <div class="flex-1 mx-2">
        <div class="h-1 rounded-full"
             [class.bg-blue-600]="currentStep() > 3"
             [class.bg-gray-200]="currentStep() <= 3">
        </div>
      </div>
      
      <!-- Step 4: Emergency Contact -->
      <div class="flex items-center">
        <div class="flex items-center justify-center w-8 h-8 rounded-full"
             [class.bg-blue-600]="currentStep() >= 4"
             [class.text-white]="currentStep() >= 4"
             [class.bg-gray-200]="currentStep() < 4"
             [class.text-gray-600]="currentStep() < 4">
          4
        </div>
        <span class="ml-2 text-sm font-medium"
              [class.text-blue-600]="currentStep() === 4"
              [class.text-gray-500]="currentStep() < 4">
          Emergency Contact
        </span>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading() && !employee()" class="flex justify-center items-center py-12">
    <sl-spinner class="text-blue-600"></sl-spinner>
    <span class="ml-3 text-gray-600">Loading employee details...</span>
  </div>

  <!-- Error Message -->
  <div *ngIf="error()" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
    <div class="flex items-center">
      <sl-icon name="exclamation-triangle" class="text-red-500 mr-2"></sl-icon>
      <span class="text-red-700">{{ error() }}</span>
    </div>
  </div>

  <!-- Form Content (only show when employee is loaded) -->
  <div *ngIf="employee() && !loading()">
    <!-- Step 1: Personal Details -->
    <div *ngIf="currentStep() === 1" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <form [formGroup]="personalDetailsForm" class="space-y-6">
        <!-- Basic Information -->
        <div>
          <h3 class="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <app-sl-input
                label="First Name *"
                type="text"
                formControlName="firstName"
                [invalid]="isFieldInvalid(personalDetailsForm, 'firstName')"
                [helpText]="getFieldError(personalDetailsForm, 'firstName')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Last Name *"
                type="text"
                formControlName="lastName"
                [invalid]="isFieldInvalid(personalDetailsForm, 'lastName')"
                [helpText]="getFieldError(personalDetailsForm, 'lastName')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Middle Name"
                type="text"
                formControlName="middleName">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Preferred Name"
                type="text"
                formControlName="preferredName">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Date of Birth *"
                type="date"
                formControlName="dateOfBirth"
                [invalid]="isFieldInvalid(personalDetailsForm, 'dateOfBirth')"
                [helpText]="getFieldError(personalDetailsForm, 'dateOfBirth')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-select
                label="Gender *"
                formControlName="gender"
                placeholder="Select gender"
                [options]="genderOptions"
                [invalid]="isFieldInvalid(personalDetailsForm, 'gender')"
                [helpText]="getFieldError(personalDetailsForm, 'gender')">
              </app-sl-select>
            </div>
            <div>
              <app-sl-select
                label="Nationality *"
                formControlName="nationality"
                placeholder="Select nationality"
                [options]="nationalityOptions"
                [invalid]="isFieldInvalid(personalDetailsForm, 'nationality')"
                [helpText]="getFieldError(personalDetailsForm, 'nationality')">
              </app-sl-select>
            </div>
            <div>
              <app-sl-select
                label="Marital Status *"
                formControlName="maritalStatus"
                placeholder="Select marital status"
                [options]="maritalStatusOptions"
                [invalid]="isFieldInvalid(personalDetailsForm, 'maritalStatus')"
                [helpText]="getFieldError(personalDetailsForm, 'maritalStatus')">
              </app-sl-select>
            </div>
          </div>
        </div>

        <!-- Contact Information -->
        <div class="border-t pt-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <app-sl-input
                label="Work Email *"
                type="email"
                formControlName="email"
                [invalid]="isFieldInvalid(personalDetailsForm, 'email')"
                [helpText]="getFieldError(personalDetailsForm, 'email')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Personal Email"
                type="email"
                formControlName="personalEmail"
                [invalid]="isFieldInvalid(personalDetailsForm, 'personalEmail')"
                [helpText]="getFieldError(personalDetailsForm, 'personalEmail')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Mobile Phone *"
                type="tel"
                formControlName="mobilePhone"
                placeholder="+44 7700 900123"
                [invalid]="isFieldInvalid(personalDetailsForm, 'mobilePhone')"
                [helpText]="getFieldError(personalDetailsForm, 'mobilePhone')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Home Phone"
                type="tel"
                formControlName="homePhone"
                placeholder="+44 20 7946 0958"
                [invalid]="isFieldInvalid(personalDetailsForm, 'homePhone')"
                [helpText]="getFieldError(personalDetailsForm, 'homePhone')">
              </app-sl-input>
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="border-t pt-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Address</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <app-sl-input
                label="Address Line 1 *"
                type="text"
                formControlName="addressLine1"
                [invalid]="isFieldInvalid(personalDetailsForm, 'addressLine1')"
                [helpText]="getFieldError(personalDetailsForm, 'addressLine1')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="City *"
                type="text"
                formControlName="city"
                [invalid]="isFieldInvalid(personalDetailsForm, 'city')"
                [helpText]="getFieldError(personalDetailsForm, 'city')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Address Line 2"
                type="text"
                formControlName="addressLine2">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="County"
                type="text"
                formControlName="county">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Postcode *"
                type="text"
                formControlName="postcode"
                placeholder="SW1A 1AA"
                [invalid]="isFieldInvalid(personalDetailsForm, 'postcode')"
                [helpText]="getFieldError(personalDetailsForm, 'postcode')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Country *"
                type="text"
                formControlName="country"
                [invalid]="isFieldInvalid(personalDetailsForm, 'country')"
                [helpText]="getFieldError(personalDetailsForm, 'country')">
              </app-sl-input>
            </div>
          </div>
        </div>

        <!-- UK Specific Information -->
        <div class="border-t pt-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">UK Specific Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <app-sl-input
                label="National Insurance Number"
                type="text"
                formControlName="nationalInsuranceNumber"
                placeholder="AB123456C"
                [invalid]="isFieldInvalid(personalDetailsForm, 'nationalInsuranceNumber')"
                [helpText]="getFieldError(personalDetailsForm, 'nationalInsuranceNumber')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Passport Number"
                type="text"
                formControlName="passportNumber">
              </app-sl-input>
            </div>
            <div>
              <app-sl-select
                label="Visa Status"
                formControlName="visaStatus"
                placeholder="Select visa status"
                [options]="visaStatusOptions">
              </app-sl-select>
            </div>
            <div>
              <app-sl-select
                label="Right to Work *"
                formControlName="rightToWork"
                placeholder="Select right to work"
                [options]="rightToWorkOptions"
                [invalid]="isFieldInvalid(personalDetailsForm, 'rightToWork')"
                [helpText]="getFieldError(personalDetailsForm, 'rightToWork')">
              </app-sl-select>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="border-t pt-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Additional Notes</h3>
          <div>
            <app-sl-textarea
              label="Notes"
              formControlName="notes"
              placeholder="Any additional notes about the employee..."
              [rows]="3">
            </app-sl-textarea>
          </div>
        </div>
      </form>
    </div>

    <!-- Step 2: Job Details -->
    <div *ngIf="currentStep() === 2" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <form [formGroup]="jobDetailsForm" class="space-y-6">
        <!-- Basic Job Information -->
        <div>
          <h3 class="text-lg font-medium text-gray-900 mb-4">Basic Job Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <app-sl-input
                label="Job Title *"
                type="text"
                formControlName="jobTitle"
                [invalid]="isFieldInvalid(jobDetailsForm, 'jobTitle')"
                [helpText]="getFieldError(jobDetailsForm, 'jobTitle')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Department *"
                type="text"
                formControlName="department"
                [invalid]="isFieldInvalid(jobDetailsForm, 'department')"
                [helpText]="getFieldError(jobDetailsForm, 'department')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Location *"
                type="text"
                formControlName="location"
                [invalid]="isFieldInvalid(jobDetailsForm, 'location')"
                [helpText]="getFieldError(jobDetailsForm, 'location')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Start Date *"
                type="date"
                formControlName="startDate"
                [invalid]="isFieldInvalid(jobDetailsForm, 'startDate')"
                [helpText]="getFieldError(jobDetailsForm, 'startDate')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Working Hours *"
                type="number"
                formControlName="workingHours"
                [invalid]="isFieldInvalid(jobDetailsForm, 'workingHours')"
                [helpText]="getFieldError(jobDetailsForm, 'workingHours')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Salary"
                type="number"
                formControlName="salary"
                [invalid]="isFieldInvalid(jobDetailsForm, 'salary')"
                [helpText]="getFieldError(jobDetailsForm, 'salary')">
              </app-sl-input>
            </div>
          </div>
        </div>

        <!-- Manager Selection -->
        <div class="border-t pt-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Reporting Structure</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <app-sl-select
                label="Manager"
                formControlName="managerId"
                placeholder="Select manager"
                [options]="managerOptions">
              </app-sl-select>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Step 3: Payee Details -->
    <div *ngIf="currentStep() === 3" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex items-center">
          <sl-icon name="info-circle" class="text-blue-500 mr-2"></sl-icon>
          <span class="text-blue-700 text-sm">
            <strong>Optional:</strong> Payee details are not mandatory. You can skip this step and add them later.
          </span>
        </div>
      </div>

      <form [formGroup]="payeeDetailsForm" class="space-y-6">
        <!-- Bank Account Information -->
        <div>
          <h3 class="text-lg font-medium text-gray-900 mb-4">Bank Account Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <app-sl-input
                label="Bank Name"
                type="text"
                formControlName="bankName">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Account Holder Name"
                type="text"
                formControlName="accountHolderName">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Account Number"
                type="text"
                formControlName="accountNumber">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Sort Code"
                type="text"
                formControlName="sortCode"
                placeholder="12-34-56"
                [invalid]="isFieldInvalid(payeeDetailsForm, 'sortCode')"
                [helpText]="getFieldError(payeeDetailsForm, 'sortCode')">
              </app-sl-input>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Step 4: Emergency Contact -->
    <div *ngIf="currentStep() === 4" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <form [formGroup]="emergencyDetailsForm" class="space-y-6">
        <!-- Emergency Contact Information -->
        <div>
          <h3 class="text-lg font-medium text-gray-900 mb-4">Emergency Contact Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <app-sl-input
                label="Name *"
                type="text"
                formControlName="emergencyContactName"
                [invalid]="isFieldInvalid(emergencyDetailsForm, 'emergencyContactName')"
                [helpText]="getFieldError(emergencyDetailsForm, 'emergencyContactName')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Relationship *"
                type="text"
                formControlName="emergencyContactRelationship"
                placeholder="Spouse, Parent, Sibling, etc."
                [invalid]="isFieldInvalid(emergencyDetailsForm, 'emergencyContactRelationship')"
                [helpText]="getFieldError(emergencyDetailsForm, 'emergencyContactRelationship')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Mobile Phone *"
                type="tel"
                formControlName="emergencyContactMobile"
                placeholder="+44 7700 900123"
                [invalid]="isFieldInvalid(emergencyDetailsForm, 'emergencyContactMobile')"
                [helpText]="getFieldError(emergencyDetailsForm, 'emergencyContactMobile')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Home Phone"
                type="tel"
                formControlName="emergencyContactHome"
                placeholder="+44 20 7946 0958"
                [invalid]="isFieldInvalid(emergencyDetailsForm, 'emergencyContactHome')"
                [helpText]="getFieldError(emergencyDetailsForm, 'emergencyContactHome')">
              </app-sl-input>
            </div>
            <div>
              <app-sl-input
                label="Email"
                type="email"
                formControlName="emergencyContactEmail"
                [invalid]="isFieldInvalid(emergencyDetailsForm, 'emergencyContactEmail')"
                [helpText]="getFieldError(emergencyDetailsForm, 'emergencyContactEmail')">
              </app-sl-input>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- Navigation Buttons -->
  <div *ngIf="employee() && !loading()" class="flex justify-between mt-8">
    <div>
      <app-sl-button
        *ngIf="currentStep() > 1"
        variant="default"
        prefixIcon="arrow-left"
        (buttonClick)="previousStep()">
        Previous
      </app-sl-button>
    </div>
    
    <div class="flex space-x-3">
      <app-sl-button
        variant="default"
        (buttonClick)="cancel()">
        Cancel
      </app-sl-button>
      
      <!-- Step 3 Skip Button -->
      <app-sl-button
        *ngIf="currentStep() === 3"
        variant="default"
        (buttonClick)="skipPayeeDetails()">
        Skip Payee Details
      </app-sl-button>
      
      <!-- Next Button for Steps 1-3 -->
      <app-sl-button
        *ngIf="currentStep() === 1"
        variant="primary"
        suffixIcon="arrow-right"
        (buttonClick)="nextStep()">
        Next: Job Details
      </app-sl-button>
      
      <app-sl-button
        *ngIf="currentStep() === 2"
        variant="primary"
        suffixIcon="arrow-right"
        (buttonClick)="nextStep()">
        Next: Payee Details
      </app-sl-button>
      
      <app-sl-button
        *ngIf="currentStep() === 3"
        variant="primary"
        suffixIcon="arrow-right"
        (buttonClick)="nextStep()">
        Next: Emergency Contact
      </app-sl-button>
      
      <!-- Submit Button for Step 4 -->
      <app-sl-button
        *ngIf="currentStep() === 4"
        variant="primary"
        prefixIcon="check"
        [loading]="loading()"
        [disabled]="emergencyDetailsForm.invalid"
        (buttonClick)="onSubmit()">
        Update Employee
      </app-sl-button>
    </div>
  </div>
</div>
